import apiClient from './apiClient';

export interface MQL {
  id: string;
  accountId: string;
  accountName: string;
  contactId?: string;
  contactName: string;
  submittedDate: string;
  score: number;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'REVIEW';
  criteria: {
    budget: boolean;
    authority: boolean;
    need: boolean;
    timeline: boolean;
  };
  notes: string;
  submittedBy: string;
  reviewedBy?: string;
  salesNotes?: string;
  salesAcceptedAt?: string;
  feedbackLog?: MQLFeedback[];
}

export interface MQLFeedback {
  id: string;
  text: string;
  author: string;
  authorId?: string | null;
  at: string;
}

export interface MQLsResponse {
  data: MQL[];
  total: number;
  page: number;
  totalPages: number;
}

export interface CreateMQLData {
  accountId: string;
  contactId?: string;
  contactName?: string;
  score: number;
  notes?: string;
  criteria: {
    budget: boolean;
    authority: boolean;
    need: boolean;
    timeline: boolean;
  };
  submittedById: string;
}

export interface UpdateMQLData {
  accountId?: string;
  contactId?: string;
  contactName?: string;
  score?: number;
  notes?: string;
  criteria?: {
    budget: boolean;
    authority: boolean;
    need: boolean;
    timeline: boolean;
  };
}

export const mqlService = {
  /**
   * Get all MQL submissions with optional filtering
   */
  async getMQLs(params?: {
    page?: number;
    limit?: number;
    status?: string;
    accountId?: string;
  }): Promise<MQLsResponse> {
    const response = await apiClient.get<MQLsResponse>('/api/mql', { params });
    return response.data;
  },

  /**
   * Get single MQL by ID
   */
  async getMQLById(id: string): Promise<MQL> {
    const response = await apiClient.get<MQL>(`/api/mql/${id}`);
    return response.data;
  },

  /**
   * Submit a new MQL
   */
  async createMQL(data: CreateMQLData): Promise<MQL> {
    const response = await apiClient.post<MQL>('/api/mql', data);
    return response.data;
  },

  /**
   * Update MQL details (not status)
   */
  async updateMQL(id: string, data: UpdateMQLData): Promise<MQL> {
    const response = await apiClient.put<MQL>(`/api/mql/${id}`, data);
    return response.data;
  },

  /**
   * Update MQL status (approve / reject / review)
   */
  async updateStatus(
    id: string,
    status: MQL['status'],
    reviewedBy?: string,
    salesNotes?: string
  ): Promise<MQL> {
    const response = await apiClient.patch<MQL>(`/api/mql/${id}/status`, {
      status,
      reviewedBy,
      salesNotes,
    });
    return response.data;
  },

  /**
   * Append a sales/BD feedback note (informational — does not change status)
   */
  async addFeedback(id: string, text: string): Promise<{ feedbackLog: MQLFeedback[]; added: MQLFeedback }> {
    const response = await apiClient.post<{ feedbackLog: MQLFeedback[]; added: MQLFeedback }>(
      `/api/mql/${id}/feedback`,
      { text }
    );
    return response.data;
  },

  /**
   * Delete an MQL submission
   */
  async deleteMQL(id: string): Promise<void> {
    await apiClient.delete(`/api/mql/${id}`);
  },
};

export default mqlService;
